import React from 'react';
import { observer } from 'mobx-react-lite';
import {
    DropzoneContainer,
    ImageContent,
    ImageThumb,
    ImageThumbAction,
    ImageThumbInner,
    ImageThumbsContainer,
    ImageThumbTools,
    Title,
} from './styles';
import DeleteIcon from '@material-ui/icons/Delete';
import { Divider, Grid, IconButton, Typography } from '@material-ui/core';
import { useDropzone } from 'react-dropzone';
import { RootStoreContext } from '../../global/storeContext';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    rectSwappingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PropertyPhotosProps {
    propertyPhotos: File[];
    setPropertyPhotos: (files: File[]) => void;
    savedPhotos: {
        id: string;
        url: string;
    }[];
    setPhotoToDelete: (photoId: string) => void;
    handleRearrangedPhotosUpdate: (photos: { id: string; url: string }[]) => void;
}

const SortablePhoto = ({
    image,
    onDelete,
}: {
    image: { id: string; url: string };
    onDelete: (image: { url: string; id: string }) => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: image.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <ImageThumb ref={setNodeRef} style={style}>
            <ImageThumbInner className='drag-handle' {...attributes} {...listeners}>
                <ImageContent src={image.url} />
            </ImageThumbInner>
            <ImageThumbTools>
                <ImageThumbAction>
                    <IconButton
                        aria-label='delete'
                        color='secondary'
                        onClick={() => onDelete(image)}
                    >
                        <DeleteIcon fontSize='small' />
                    </IconButton>
                </ImageThumbAction>
            </ImageThumbTools>
        </ImageThumb>
    );
};

const ItemOverlay = ({
    id,
    photos,
}: {
    id: string | number;
    photos: { id: string; url: string }[];
}) => {
    const image = photos.find((p) => p.id === id);

    if (!image) return null;

    return (
        <ImageThumb style={{ boxShadow: '0 0 10px rgba(0,0,0,0.5)' }}>
            <ImageThumbInner>
                <ImageContent src={image.url} />
            </ImageThumbInner>
        </ImageThumb>
    );
};

export const PropertyPhotos: React.FC<PropertyPhotosProps> = observer(
    ({
        propertyPhotos,
        setPropertyPhotos,
        savedPhotos,
        setPhotoToDelete,
        handleRearrangedPhotosUpdate,
    }) => {
        const { uiStore } = React.useContext(RootStoreContext);

        const [activeId, setActiveId] = React.useState<string | number | null>(null);

        const generatePreview = (files: File[]) => {
            return files.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                }),
            );
        };

        const [files, setFiles] = React.useState<
            (File & {
                preview: string;
            })[]
        >([...generatePreview(propertyPhotos)]);

        const [photos, setPhotos] = React.useState(savedPhotos ?? []);

        const sensors = useSensors(
            useSensor(PointerSensor),
            useSensor(KeyboardSensor, {
                coordinateGetter: sortableKeyboardCoordinates,
            }),
        );

        const onDrop = (acceptedFiles: File[]) => {
            const checkedFiles = acceptedFiles
                .map((acceptedFile) => {
                    const fileSize = acceptedFile.size / 1024;
                    if (fileSize > 3072) {
                        uiStore.error('File too large. Picture size must not be over 3mb');
                        return null;
                    }
                    return acceptedFile;
                })
                .filter((file) => file !== null) as File[];

            var newFiles = [...propertyPhotos, ...checkedFiles];
            // Make sure to revoke the data uris to avoid memory leaks
            files.forEach((file: any) => URL.revokeObjectURL(file.preview));
            setFiles([...generatePreview(newFiles)]);
            setPropertyPhotos([...newFiles]);
        };

        const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
            useDropzone({
                accept: {
                    'image/*': [],
                },
                onDrop,
            });

        const removePhoto = (file: any) => {
            const index = propertyPhotos.findIndex((f) => f.name === file.name);
            if (index >= 0) {
                var temp = [...propertyPhotos];
                temp.splice(index, 1);

                setPropertyPhotos([...temp]);
                setFiles([...generatePreview(temp)]);
            }
        };

        const deletePhoto = (image: { url: string; id: string }) => {
            if (!!image) {
                setPhotos(photos.filter((x) => x.id !== image.id));
                setPhotoToDelete(image.id);
            }
        };

        const imageThumbs = files.map((file: any) => (
            <ImageThumb key={file.name}>
                <ImageThumbInner>
                    <ImageContent src={file.preview} />
                </ImageThumbInner>
                <ImageThumbTools>
                    <ImageThumbAction>
                        <IconButton
                            aria-label='delete'
                            color='secondary'
                            onClick={() => {
                                removePhoto(file);
                            }}
                        >
                            <DeleteIcon fontSize='small' />
                        </IconButton>
                    </ImageThumbAction>
                </ImageThumbTools>
            </ImageThumb>
        ));

        function handleDragStart(event: DragStartEvent) {
            const { active } = event;

            setActiveId(active.id);
        }

        const handleDragEnd = (event: DragEndEvent) => {
            const { active, over } = event;

            if (active.id !== over?.id) {
                setPhotos((photo) => {
                    const oldIndex = photos.findIndex((item) => item.id === active.id);
                    const newIndex = photos.findIndex((item) => item.id === over?.id);

                    const newArr = arrayMove(photo, oldIndex, newIndex);
                    handleRearrangedPhotosUpdate(newArr);
                    return newArr;
                });
            }
        };

        const imagePreview = (
            <>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={photos.map((item) => item.id)}
                        strategy={rectSwappingStrategy}
                    >
                        {photos.map((image: any) => (
                            <SortablePhoto image={image} onDelete={deletePhoto} />
                        ))}
                    </SortableContext>
                    <DragOverlay>
                        {activeId ? <ItemOverlay id={activeId} photos={photos} /> : null}
                    </DragOverlay>
                </DndContext>
            </>
        );

        return (
            <>
                <Title>
                    <Typography variant='h5' gutterBottom>
                        Add photos of your property.
                    </Typography>
                </Title>

                <form noValidate autoComplete='off'>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DropzoneContainer
                                {...getRootProps({ isDragActive, isDragAccept, isDragReject })}
                            >
                                <input {...getInputProps()} />
                                {isDragActive ? (
                                    <p>Drop the files here ...</p>
                                ) : (
                                    <p>Drag 'n' drop some files here, or click to select files</p>
                                )}
                            </DropzoneContainer>
                        </Grid>

                        <Grid item xs={12}>
                            <ImageThumbsContainer>{imageThumbs}</ImageThumbsContainer>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider variant='fullWidth' />
                        </Grid>
                        <Grid item xs={12}>
                            {photos ? (
                                <ImageThumbsContainer>{imagePreview}</ImageThumbsContainer>
                            ) : null}
                        </Grid>
                    </Grid>
                </form>
            </>
        );
    },
);
